import axios, { AxiosError, AxiosInstance } from "axios";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { ensureValidAuth, getAuthState, clearAuth } from "./auth";

// Get API client with auth headers
export function getApiClient(): AxiosInstance {
  const { eoaAddress, apiKey, chainID, baseUrl, apiPath, isInitialized } =
    useSDKConfigStore.getState();

  if (!isInitialized || !apiKey) {
    throw new Error("SwypeSDK not initialized. Call initializeSwypeSDK first.");
  }

  const authState = getAuthState();
  if (!authState.signature || !authState.deadline) {
    throw new Error(
      "SwypeSDK auth not ready. Authentication signature missing."
    );
  }

  const fullBaseURL = `${baseUrl}${apiPath}`;

  // Encode in base 64
  const authJson = {
    timestamp: authState.deadline,
    sig: authState.signature,
    userAddr: eoaAddress,
    chainID,
  };

  const encodedAuth = btoa(JSON.stringify(authJson));

  const client = axios.create({
    baseURL: fullBaseURL,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      Authorization: `Bearer ${encodedAuth}`,
      "ngrok-skip-browser-warning": "true", // Skip ngrok browser warning
    },
  });

  // Add request interceptor to ensure valid auth before each request
  client.interceptors.request.use(
    async (config) => {
      await ensureValidAuth();

      // Update headers with fresh auth if regenerated
      const freshAuthState = getAuthState();
      if (freshAuthState.signature && freshAuthState.deadline) {
        config.headers.set("Authorization", `Bearer ${encodedAuth}`);
        config.headers.set("x-api-key", apiKey);
        config.headers.set("ngrok-skip-browser-warning", "true");
        // config.headers.set("X-Deadline", freshAuthState.deadline.toString());
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for handling errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response) {
        console.error("API Error Response:", {
          status: error.response.status,
          data: error.response.data,
        });

        // If auth error, clear auth state to force regeneration
        if (error.response.status === 401 || error.response.status === 403) {
          clearAuth();
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}
